"use client";

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Switch,
  Chip,
  Stack,
} from "@mui/material";

import { apiService } from "../../app/api";
import { handleAxiosError } from "../../helper/errorHandler";
import { fetchLanguages } from "../../helper/languageUtils";
import MultiSelectDropdown from "../MultiSelectDropdown";
import { FEATURE_UI_CONFIG } from "./featureUiConfig";
import { API_FEATURE_LABELS, FEATURE_UNIT } from "./featureMap";

interface ApiAccess {
  _id: string;
  feature: string;
  input_languages: string[];
  output_languages?: string[];
  avatars?: string[];
  usage_limit: number;
  usage_limit_unit: "minutes" | "characters";
  start_date?: string;
  end_date: string;
  active: boolean;
}

interface Props {
  user_id: string;
}

const APIPermissions = ({ user_id }: Props) => {
  const today = new Date().toLocaleDateString("en-CA");

  const formRef = useRef<HTMLDivElement | null>(null);
  const [permissions, setPermissions] = useState<ApiAccess[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [avatars, setAvatars] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [form, setForm] = useState({
    feature: "",
    input_languages: [] as string[],
    output_languages: [] as string[],
    avatars: [] as string[],
    usage_limit: "",
    usage_limit_unit: "minutes" as "minutes" | "characters",
    start_date: today,
    end_date: "",
  });

  const featureConfig = FEATURE_UI_CONFIG[form.feature];
  const isVoiceCloningFeature = form.feature.includes("_vc");

  useEffect(() => {
    loadLanguages();
    loadPermissions();
    fetchAvatars();
  }, []);

  const loadLanguages = async () => {
    const langs = await fetchLanguages();
    setLanguages(langs);
  };

  const fetchAvatars = async () => {
    try {
      const { data } = await apiService.get(
        "/avatar?is_active=true&page=1&limit=100&fields=avatar"
      );
      setAvatars(data.data.map((a: any) => a.avatar));
    } catch (err) {
      handleAxiosError(err);
    }
  };

  const loadPermissions = async () => {
    try {
      const res = await apiService.get(`/user-api-access/${user_id}`);
      setPermissions(res.data);
    } catch (err) {
      handleAxiosError(err);
    }
  };

  const createPermission = async () => {
    try {
      await apiService.post("/user-api-access", {
        user_id,
        feature: form.feature,
        input_languages: form.input_languages,
        output_languages: form.output_languages,
        avatars: form.avatars,
        usage_limit: Number(form.usage_limit),
        usage_limit_unit: form.usage_limit_unit,
        start_date: form.start_date,
        end_date: form.end_date,
      });

      loadPermissions();

      setForm({
        feature: "",
        input_languages: [],
        output_languages: [],
        avatars: [],
        usage_limit: "",
        usage_limit_unit: "minutes",
        start_date: today,
        end_date: "",
      });
    } catch (err) {
      handleAxiosError(err);
    }
  };

  const toggleActive = async (id: string, active: boolean) => {
    try {
      await apiService.patch(`/user-api-access/${id}/active`, { active });
      loadPermissions();
    } catch (err) {
      handleAxiosError(err);
    }
  };

  useEffect(() => {
    if (!isVoiceCloningFeature && form.avatars.length) {
      setForm((prev) => ({ ...prev, avatars: [] }));
    }

    if (form.feature) {
      const unit = FEATURE_UNIT[form.feature];

      if (unit) {
        setForm((prev) => ({
          ...prev,
          usage_limit_unit: unit,
        }));
      }
    }
  }, [form.feature]);

  const addedFeatures = new Set(permissions.map((p) => p.feature));
  const availableFeatures = Object.entries(API_FEATURE_LABELS).filter(
    ([key]) => !addedFeatures.has(key)
  );

  // edit
  const handleEdit = (p: ApiAccess) => {
    setEditingId(p._id);

    setForm({
      feature: p.feature,
      input_languages: p.input_languages || [],
      output_languages: p.output_languages || [],
      avatars: p.avatars || [],
      usage_limit: String(p.usage_limit),
      usage_limit_unit: p.usage_limit_unit,
      start_date: p.start_date ? p.start_date.slice(0, 10) : today,
      end_date: p.end_date.slice(0, 10),
    });

    setTimeout(() => {
      formRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const updatePermission = async () => {
    if (!editingId) return;

    try {
      await apiService.put(`/user-api-access/${editingId}`, {
        feature: form.feature,
        input_languages: form.input_languages,
        output_languages: form.output_languages,
        avatars: form.avatars,
        usage_limit: Number(form.usage_limit),
        usage_limit_unit: form.usage_limit_unit,
        start_date: form.start_date,
        end_date: form.end_date,
      });

      loadPermissions();
      resetForm();
    } catch (err) {
      handleAxiosError(err);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      feature: "",
      input_languages: [],
      output_languages: [],
      avatars: [],
      usage_limit: "",
      usage_limit_unit: "minutes",
      start_date: today,
      end_date: "",
    });
  };

  return (
    <Box>
      <Typography variant="h6" mb={3}>
        API Permissions
      </Typography>

      {/* -------------------- Create Form -------------------- */}
      <Card sx={{ mb: 4 }} ref={formRef}>
        <CardContent>
          <Typography variant="subtitle1" mb={2}>
            Grant API Access
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3.5}>
              {editingId ? (
                <TextField
                  label="Feature"
                  value={API_FEATURE_LABELS[form.feature] ?? form.feature}
                  fullWidth
                  size="small"
                  disabled
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        locked
                      </Typography>
                    ),
                  }}
                  helperText="Feature cannot be changed once assigned"
                />
              ) : (
                <TextField
                  select
                  label="Feature"
                  value={form.feature}
                  onChange={(e) =>
                    setForm({ ...form, feature: e.target.value })
                  }
                  fullWidth
                  size="small"
                  disabled={availableFeatures.length === 0}
                >
                  {availableFeatures.map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}

                  {availableFeatures.length === 0 && (
                    <MenuItem disabled value="">
                      All features already assigned
                    </MenuItem>
                  )}
                </TextField>
              )}
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                label="Limit"
                type="number"
                value={form.usage_limit}
                onChange={(e) =>
                  setForm({ ...form, usage_limit: e.target.value })
                }
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={6} md={1.5}>
              <TextField
                select
                label="Unit"
                value={form.usage_limit_unit}
                fullWidth
                size="small"
                disabled
              >
                <MenuItem value="minutes">Minutes</MenuItem>
                <MenuItem value="characters">Characters</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="Start Date"
                type="date"
                value={form.start_date}
                onChange={(e) =>
                  setForm({ ...form, start_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                inputProps={{
                  min: today,
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="End Date"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                inputProps={{
                  min: form.start_date || today,
                }}
              />
            </Grid>

            {(featureConfig?.showInput ||
              featureConfig?.showOutput ||
              isVoiceCloningFeature) && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {featureConfig?.showInput && (
                    <Grid item xs={12} md={6}>
                      <MultiSelectDropdown
                        options={languages}
                        value={form.input_languages}
                        onChange={(v) =>
                          setForm({ ...form, input_languages: v })
                        }
                        label={featureConfig.inputLabel}
                      />
                    </Grid>
                  )}

                  {featureConfig?.showOutput && (
                    <Grid item xs={12} md={6}>
                      <MultiSelectDropdown
                        options={languages}
                        value={form.output_languages}
                        onChange={(v) =>
                          setForm({ ...form, output_languages: v })
                        }
                        label={featureConfig.outputLabel}
                      />
                    </Grid>
                  )}

                  {isVoiceCloningFeature && (
                    <Grid item xs={12} md={6}>
                      <MultiSelectDropdown
                        options={avatars}
                        value={form.avatars}
                        onChange={(v) => setForm({ ...form, avatars: v })}
                        label="Avatar"
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={editingId ? updatePermission : createPermission}
                >
                  {editingId ? "Update Access" : "Grant Access"}
                </Button>

                {editingId && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* -------------------- Permissions List -------------------- */}
      {permissions.map((p) => {
        const cfg = FEATURE_UI_CONFIG[p.feature];

        return (
          <Card key={p._id} sx={{ mb: 2, opacity: p.active ? 1 : 0.6 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography fontWeight={600}>
                  {API_FEATURE_LABELS[p.feature] ?? p.feature}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleEdit(p)}
                  >
                    Edit
                  </Button>

                  <Switch
                    checked={p.active}
                    onChange={(e) => toggleActive(p._id, e.target.checked)}
                  />
                </Stack>
              </Stack>

              <Typography variant="body2">
                Limit: <strong>{p.usage_limit}</strong> {p.usage_limit_unit}
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={2}>
                Start:{" "}
                <strong>
                  {p.start_date
                    ? new Date(p.start_date).toLocaleDateString()
                    : "—"}
                </strong>
                {" • "}
                End:{" "}
                <strong>{new Date(p.end_date).toLocaleDateString()}</strong>
              </Typography>

              {(cfg?.showInput || cfg?.showOutput) && (
                <Grid container spacing={2}>
                  {cfg?.showInput && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption">
                        {cfg.inputLabel}
                      </Typography>
                      <Stack direction="row" gap={0.5} mt={1} flexWrap="wrap">
                        {p.input_languages.map((l) => (
                          <Chip key={l} label={l} size="small" />
                        ))}
                      </Stack>
                    </Grid>
                  )}

                  {cfg?.showOutput && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption">
                        {cfg.outputLabel}
                      </Typography>
                      <Stack direction="row" gap={0.5} mt={1} flexWrap="wrap">
                        {p.output_languages?.map((l) => (
                          <Chip key={l} label={l} size="small" />
                        ))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              )}

              {p.avatars && p.avatars?.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption">Avatars</Typography>
                  <Stack direction="row" gap={0.5} mt={1} flexWrap="wrap">
                    {p.avatars.map((a) => (
                      <Chip key={a} label={a} size="small" />
                    ))}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default APIPermissions;
